import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dumbbell, Copy, Crown, Shield, User as UserIcon, Plus, LogIn, Check, UserMinus, LogOut } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", icon: Crown, color: "#E63946" },
  coach: { label: "Coach", icon: Shield, color: "#007AFF" },
  user: { label: "Member", icon: UserIcon, color: "#A1A1AA" },
  individual: { label: "Individual", icon: UserIcon, color: "#A1A1AA" },
};

export default function Gym() {
  const { user, refreshUser } = useAuth();
  const [gym, setGym] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gyms/me");
      setGym(data);
      if (data) { const m = await api.get("/gyms/members"); setMembers(m.data); }
    } catch (e) { /* no gym */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createGym = async () => {
    if (!createForm.name.trim()) { toast.error("Gym name required"); return; }
    setBusy(true);
    try { await api.post("/gyms", createForm); await refreshUser(); await load(); toast.success("Gym created — you're an admin!"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const joinGym = async () => {
    if (!joinCode.trim()) { toast.error("Enter an invite code"); return; }
    setBusy(true);
    try { await api.post("/gyms/join", { invite_code: joinCode }); await refreshUser(); await load(); toast.success("Joined gym!"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const copyCode = () => { navigator.clipboard.writeText(gym.invite_code); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const changeRole = async (memberId, role) => {
    try { await api.patch(`/gyms/members/${memberId}/role`, { role }); await load(); toast.success("Role updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (memberId) => {
    try { await api.delete(`/gyms/members/${memberId}`); await load(); toast.success("Member removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const leaveGym = async () => {
    try {
      await api.post("/gyms/leave");
      await refreshUser();
      await load();
      toast.success("You left the gym");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="p-8"><div className="h-64 rounded-xl bg-card border border-border animate-pulse" /></div>;

  // No gym view
  if (!gym) {
    return (
      <div className="p-6 md:p-8 pb-24 md:pb-8 max-w-3xl">
        <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">Your gym</p>
        <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">Join the mat</h1>
        <p className="text-muted-foreground mb-8">Create your own gym to build a shared library, or join an existing one with an invite code.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="p-6 rounded-xl bg-card border border-border">
            <Dumbbell className="h-7 w-7 text-primary mb-3" />
            <h3 className="font-heading font-semibold text-lg mb-4">Create a gym</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Gym name</Label>
                <Input data-testid="create-gym-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="mt-1.5 bg-background border-border" placeholder="Alliance HQ" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea data-testid="create-gym-desc" value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} className="mt-1.5 bg-background border-border" placeholder="Optional" />
              </div>
              <Button data-testid="create-gym-btn" onClick={createGym} disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-white"><Plus className="h-4 w-4 mr-1.5" /> Create gym</Button>
            </div>
          </div>
          <div className="p-6 rounded-xl bg-card border border-border">
            <LogIn className="h-7 w-7 text-primary mb-3" />
            <h3 className="font-heading font-semibold text-lg mb-4">Join a gym</h3>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Invite code</Label>
            <Input data-testid="join-gym-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="mt-1.5 bg-background border-border font-mono tracking-widest" placeholder="A1B2C3D4" />
            <Button data-testid="join-gym-btn" onClick={joinGym} disabled={busy} variant="outline" className="w-full mt-3 border-border"><LogIn className="h-4 w-4 mr-1.5" /> Join gym</Button>
          </div>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";

  // Has gym view
  return (
    <div className="p-6 md:p-8 pb-24 md:pb-8 max-w-4xl">
      <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">Your gym</p>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">{gym.name}</h1>
          {gym.description && <p className="text-muted-foreground mt-1 max-w-lg">{gym.description}</p>}
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Invite code</p>
          <div className="flex items-center gap-2">
            <code data-testid="gym-invite-code" className="font-mono text-lg tracking-[0.2em] text-primary">{gym.invite_code}</code>
            <Button data-testid="copy-invite-btn" size="icon" variant="ghost" onClick={copyCode} className="h-8 w-8 text-muted-foreground hover:text-primary">
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Share to invite members</p>
        </div>
      </div>

      <h2 className="font-heading font-semibold text-lg mb-4">Members ({members.length})</h2>
      <div className="space-y-2">
        {members.map((m) => {
          const rm = ROLE_META[m.role] || ROLE_META.user;
          const self = m.user_id === user?.user_id;
          return (
            <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border">
              <Avatar className="h-10 w-10">
                <AvatarImage src={m.picture} />
                <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{(m.name || m.email).slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{m.name || m.email} {self && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: rm.color }}><rm.icon className="h-3.5 w-3.5" /> {rm.label}</span>
              {isAdmin && !self && (
                <div className="flex items-center gap-1.5">
                  <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                    <SelectTrigger data-testid={`role-select-${m.user_id}`} className="h-8 w-28 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="user">Member</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid={`remove-member-${m.user_id}`} size="icon" variant="ghost" onClick={() => removeMember(m.user_id)} className="h-8 w-8 text-muted-foreground hover:text-destructive"><UserMinus className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(isAdmin || user?.role === "coach") && (
        <p className="text-sm text-muted-foreground mt-6">As {ROLE_META[user.role].label.toLowerCase()}, you can add and share gym-wide techniques and sequences visible to all members.</p>
      )}

      <div className="mt-10 pt-6 border-t border-border">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="leave-gym-btn" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive hover:text-white">
              <LogOut className="h-4 w-4 mr-2" /> Leave gym
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Leave {gym.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll lose access to this gym's shared techniques and sequences and become an individual user.
                Your personal (private) content stays with you. You can rejoin later with an invite code.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-leave-gym" onClick={leaveGym} className="bg-destructive hover:bg-destructive/90 text-white">Leave gym</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
